import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  useGetCompanyProcessedData,
  useGetMcaProcessedPreSignedURL,
  useGetPreSignedURL,
} from "../../queries/onboarding";
import Loader from "../../utils/loader";

type Props = {
  cin: string;
  showPopup: boolean;
  onClose: () => void;
  selectedMgt7: string;
  selectedPas3: string;
  onSave: Function;
};
type FileCardProps = {
  fileName: string;
  fileUrl: string;
  setIsSignedUrl: (url: string) => void;
};
export const FinalDocumentsPopup = ({
  showPopup,
  onClose,
  cin,
  selectedMgt7,
  selectedPas3,
  onSave,
}: Props) => {
  const [isSignedUrl, setIsSignedUrl] = useState<string>();
  const { data: signedUrl, refetch } = useGetMcaProcessedPreSignedURL(
    isSignedUrl || "",
    isSignedUrl !== undefined,
  );

  const [isInputSignedUrl, setIsInputSignedUrl] = useState<string>();
  const { data: signedUrl1, refetch: refetchInput } = useGetPreSignedURL(
    isInputSignedUrl || "",
    isInputSignedUrl !== undefined,
  );
  const { data, isFetched } = useGetCompanyProcessedData(cin);

  useEffect(() => {
    if (!isSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetch();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isSignedUrl]);

  useEffect(() => {
    if (!isInputSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetchInput();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isInputSignedUrl]);

  const FileCard: React.FC<FileCardProps> = ({
    fileName,
    fileUrl,
    setIsSignedUrl,
  }) => {
    const excelImgSrc =
      "https://upload.wikimedia.org/wikipedia/commons/3/34/Microsoft_Office_Excel_%282019%E2%80%93present%29.svg";
    const pdfImgSrc =
      "https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg";

    const getIconSrc = (fileName: string) => {
      const extension = fileName.split(".").pop()?.toLowerCase();
      if (extension === "pdf") return pdfImgSrc;
      if (extension === "xls" || extension === "xlsx") return excelImgSrc;
      return ""; // fallback or generic icon if needed
    };

    const iconSrc = getIconSrc(fileName);

    return (
      <div className="w-full h-[65px] bg-gray-100 rounded-md shadow-md p-4 border border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img src={iconSrc} alt="File" className="w-8 h-8 object-contain" />
          <span className="text-sm font-medium text-gray-800 truncate w-100">
            {fileName}
          </span>
        </div>
        <button
          onClick={() => {
            setIsSignedUrl(fileUrl);
          }}
          className="flex items-center gap-1 text-sm mt-2 bg-blue-600 hover:bg-blue-800 border px-2 py-1 rounded-md text-white"
        >
          <Download className="w-4 h-4 text-white flex-shrink-0 rounded-lg" />
          Download
        </button>
      </div>
    );
  };

  return (
    <Transition show={showPopup} as={Fragment}>
      <Dialog as="div" className="relative z-10 w-[500px]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="w-3/6 h-[475px] transform overflow-auto rounded-lg bg-white p-6 shadow-xl transition-all">
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold leading-6 text-gray-900"
                  >
                    <div className="flex justify-between items-center">
                      <h2>Documents</h2>
                      <X
                        className="text-gray-500 hover:text-blue-500 cursor-pointer"
                        onClick={onClose}
                      />
                    </div>
                  </DialogTitle>
                  <div className="">
                    <div>
                      {isFetched ? (
                        <>
                          <p className="text-sm text-gray-500 font-bold">
                            {data?.confidence &&
                              `Confidence: ${data?.confidence}%`}
                          </p>
                          <div className="flex flex-wrap gap-4 my-4">
                            <FileCard
                              fileName={data.excelUrl.split("/").pop() || ""}
                              fileUrl={data.excelUrl}
                              setIsSignedUrl={setIsSignedUrl}
                            />
                            <FileCard
                              fileName={data.loaUrl.split("/").pop() || ""}
                              fileUrl={data.loaUrl}
                              setIsSignedUrl={setIsSignedUrl}
                            />
                            <FileCard
                              fileName={selectedPas3?.split("/").pop() || ""}
                              fileUrl={selectedPas3}
                              setIsSignedUrl={setIsInputSignedUrl}
                            />
                            <FileCard
                              fileName={selectedMgt7?.split("/").pop() || ""}
                              fileUrl={selectedMgt7}
                              setIsSignedUrl={setIsInputSignedUrl}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="h-[600px]">
                          <Loader />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
